import Point from './Point';
import Particle from './Particle';
import { RAD, TAU } from '@CS/utils/Number';
import { randomInt, random, clamp } from '@CS/utils/Number';

export default class Triangle extends Particle {
  private readonly size = { min: 10.0, max: 25.0 };
  private readonly length = randomInt(this.size.min, this.size.max);
  private readonly diameter = this.length << 1;

  private readonly bottomCathetus = randomInt(this.length, this.diameter);
  private readonly leftCathetus = randomInt(this.length, this.diameter);

  private rotationSpeed = randomInt(1.0, 10.0) * 0.01;
  private dop = randomInt(1.0, 10.0) * 0.01;
  private rotation = random(0.0, TAU);

  private readonly speed = new Point(
    randomInt(-RAD, RAD),
    Math.min(this.size.max - this.length + 2.5, this.size.min)
  );

  private readonly max = new Point(
    this.width + this.length,
    this.height + this.length
  );

  protected override opacity = 0.0;

  public constructor(context: CanvasRenderingContext2D) {
    super(context);
  }

  public override start(): void {
    super.start();
    this.opacity = 0;

    this.rotation = random(0, RAD);
    this.dop = randomInt(1, 10) * 0.01;

    this.max.x = this.width + this.length;
    this.max.y = this.height + this.length;

    this.rotationSpeed = randomInt(1, 10) * 0.01;

    this.position.x = random(-this.diameter, this.width + this.length);
    this.position.y = random(this.height * -0.5, -this.diameter);

    const { min, max } = this.size;
    this.speed.x = randomInt(-RAD, RAD);
    this.speed.y = Math.min(max - this.length + 2.5, min);

    this.max.x = this.width + this.length;
    this.max.y = this.height + this.length;
  }

  public override stop(): void {
    super.stop();
    ~Math.sign(this.dop) && (this.dop *= -1);
  }

  public override draw(): void {
    this.position.x += this.speed.x;
    this.position.y += this.speed.y;

    this.opacity = clamp(this.opacity + this.dop);
    this.rotation = (this.rotation + this.rotationSpeed) % TAU;

    if (!this.opacity || this.position.y > this.max.y) {
      if (!this.running) return;
      this.start();
    }

    if (!(this.position.x && this.position.x < this.max.x))
      this.position.x = (this.position.x + this.max.x) % this.max.x;

    this.opacity === 1 && (this.dop *= -1);

    this.context.save();

    const lx = (this.position.x | 0) - this.length;
    const ly = (this.position.y | 0) - this.length;

    const rx = lx + this.bottomCathetus;
    const by = ly + this.leftCathetus;

    const cx = (lx * 2.0 + rx) / 3.0;
    const cy = (ly + by * 2.0) / 3.0;

    this.context.translate(cx, cy);
    this.context.rotate(this.rotation);
    this.context.translate(-cx, -cy);

    this.drawTriangle(lx, ly, by, rx);
    this.context.restore();
  }

  private drawTriangle(lx: number, ly: number, by: number, rx: number): void {
    this.context.beginPath();
    const alpha = this.opacity.toFixed(2);
    this.context.fillStyle = `rgba(${this.rgb}, ${alpha})`;

    this.context.moveTo(lx, ly);
    this.context.lineTo(lx, by);
    this.context.lineTo(rx, by);

    this.context.closePath();
    this.context.fill();
  }
}
